// /src/screens/OnboardingScreen.js
import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import bg from "../@shared/assets/mydecksbg.png";
import Toast from "../components/Toast";
import { DarkSurface, GlassIconButton, SegmentedControl } from "../components/ui";
import { colors, displayTextSx, eyebrowTextSx, glassPanelSx } from "../@shared/ui/tokens";
import { ArrowBackIosNewRounded } from "@mui/icons-material";
import { MainContext } from "../App";
import {
  useAuthStore,
  uploadImageAndGetUrl,
} from "../@shared/store/useAuthStore";
import { validateAuthInputs } from "../@shared/utils/Utils";
import { CTOS, IPTOS } from "../@shared/utils/TOS";
import { stripeConnectCountryCodes } from "../@shared/utils/countryCodes";

import { auth, db } from "../@shared/firebase/config";
import {
  doc,
  updateDoc,
  getDoc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { sendEmailVerification, onAuthStateChanged } from "firebase/auth";
import { httpsCallable, getFunctions } from "firebase/functions";
import confetti from "canvas-confetti";
import { useNavigate } from "react-router-dom";

const FLOW = {
  EXISTING_COLLECTION: "existing_collection",
  NEW_COLLECTION: "new_collection",
  IP_PROVIDER: "ip_provider",
};

const AUTH_MODE = {
  SIGN_IN: "sign_in",
  SIGN_UP: "sign_up",
};

const OnboardingScreen = () => {
  const navigate = useNavigate();
  const { Theme, setError, setSuccess, setMessage } = useContext(MainContext);

  const { login, signUp, userData } = useAuthStore();

  // local auth readiness (prevents “blank” flashes)
  const [authReady, setAuthReady] = useState(false);

  const [enrollmentFlow, setEnrollmentFlow] = useState(FLOW.IP_PROVIDER);
  const [authMode, setAuthMode] = useState(AUTH_MODE.SIGN_IN);

  const [loading, setLoading] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);

  const [tosScrolled, setTosScrolled] = useState(false);
  const [country, setCountry] = useState("");

  const [stripeComplete, setStripeComplete] = useState(false);
  const [completionWritePending, setCompletionWritePending] = useState(false);
  const [creatorCollectionMeta, setCreatorCollectionMeta] = useState({
    exists: false,
    logoUrl: "",
    bannerUrl: "",
  });

  const [form, setForm] = useState({
    fullName: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",

    // creator details
    xHandle: "",
    collectionName: "",
    logoFile: null,
    bannerFile: null,
    discord: "",
    mintPage: "",
  });

  // ---------------------------
  // Derived flags (single source)
  // ---------------------------
  const isSignedIn = !!userData?.id;
  const roles = Array.isArray(userData?.roles) ? userData.roles : [];
  const isCreator = roles.includes("collection_creator");
  const isIP = roles.includes("ip_provider");
  const emailVerified = !!userData?.emailVerified;
  const effectiveCountry = country || userData?.country || "";
  const creatorNameValue = (userData?.collectionName || "").trim();
  const creatorHandleValue = (userData?.xHandle || "").trim();
  const creatorLogoValue =
    creatorCollectionMeta.logoUrl ||
    userData?.collection?.logoUrl ||
    "";
  const hasCreatorDetails =
    !!creatorNameValue &&
    !!creatorHandleValue &&
    !!creatorLogoValue;

  const whiteFieldStyle = {
    mb: 2,
    input: { color: "#000" },
    label: { color: "#999" },
    backgroundColor: "#fff",
    "&:hover": { backgroundColor: "#fff" },
    "& .MuiFilledInput-root": {
      backgroundColor: "#fff",
      "&:hover": { backgroundColor: "#fff" },
    },
  };

  const sectionCardSx = {
    p: 2,
    borderRadius: "16px",
    bgcolor: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.1)",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06)",
    textAlign: "left",
  };

  const sanitizeUrl = (v) => {
    if (!v) return "";
    const trimmed = v.trim();
    if (!trimmed) return "";
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const handleChange = useCallback(
    (field) => (e) => {
      const value = e.target.value;
      setForm((p) => ({ ...p, [field]: value }));
    },
    []
  );

  const handleFile = useCallback(
    (field) => (e) => {
      const file = e.target.files?.[0] || null;
      setForm((p) => ({ ...p, [field]: file }));
    },
    []
  );

  // ---------------------------
  // Auth readiness
  // ---------------------------
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, () => setAuthReady(true));
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!userData?.id) {
      setCreatorCollectionMeta({ exists: false, logoUrl: "", bannerUrl: "" });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const collectionRef = doc(db, "collections_onboarding", userData.id);
        const snap = await getDoc(collectionRef);
        if (cancelled) return;
        const data = snap.exists() ? snap.data() || {} : {};
        setCreatorCollectionMeta({
          exists: snap.exists(),
          logoUrl: data.logoUrl || "",
          bannerUrl: data.bannerUrl || "",
        });
      } catch (e) {
        console.error("Failed to load creator collection meta:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userData?.id]);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      collectionName: prev.collectionName || userData?.collectionName || "",
      xHandle: prev.xHandle || userData?.xHandle || "",
      discord: prev.discord || userData?.discord || "",
      mintPage: prev.mintPage || userData?.mintPage || "",
    }));
  }, [
    userData?.collectionName,
    userData?.xHandle,
    userData?.discord,
    userData?.mintPage,
  ]);

  // ---------------------------
  // Stripe status
  // ---------------------------
  useEffect(() => {
    const verifyStripe = async () => {
      setStripeComplete(false);

      const isLocal = window.location.origin.includes("localhost");
      const stripeIdToCheck = isLocal
        ? userData?.stripeTestId
        : userData?.stripeId;

      if (!stripeIdToCheck) return;

      try {
        const functions = getFunctions();
        const checkStatus = httpsCallable(
          functions,
          "checkStripeOnboardingStatus"
        );

        const res = await checkStatus({
          stripeId: stripeIdToCheck,
          test: isLocal,
        });

        const { details_submitted } = res.data || {};
        setStripeComplete(!!details_submitted);
      } catch (e) {
        console.error("Stripe verification error:", e);
        setStripeComplete(false);
      }
    };

    verifyStripe();
  }, [userData?.stripeId, userData?.stripeTestId]);

  // Reset step-specific UI when switching flow
  useEffect(() => {
    setTosScrolled(false);
    setCountry(userData?.country || "");
    // default auth mode: signed out -> sign in first
    if (!isSignedIn) setAuthMode(AUTH_MODE.SIGN_IN);
  }, [enrollmentFlow, isSignedIn, userData?.country]);



useEffect(() => {
  const unsub = onAuthStateChanged(auth, async (user) => {
    if (!user) return;

    // IMPORTANT: refresh the token/user object so emailVerified is accurate
    await user.reload();

    if (user.emailVerified && userData?.id && !userData?.emailVerified) {
      try {
        await updateDoc(doc(db, "users", userData.id), {
          emailVerified: true,
          updatedAt: new Date(),
        });

        // If your store doesn’t auto-refetch, trigger whatever you use
        // e.g. useAuthStore.getState().refreshUserData?.()
      } catch (e) {
        console.error("Failed to sync emailVerified:", e);
      }
    }
  });

  return () => unsub();
}, [userData?.id, userData?.emailVerified]);


  // ---------------------------
  // Step machine
  // ---------------------------
  const step = useMemo(() => {
    // If auth is not ready, show loader
    if (!authReady) return "loading";

    // --- IP PROVIDER FLOW ---
    if (enrollmentFlow === FLOW.IP_PROVIDER) {
      if (!isSignedIn) return "auth";

      if (!emailVerified) return "verify_email";

      // if not IP role yet -> add it (this is the missing state in your current code)
      if (!isIP) return "add_role";

      if (!effectiveCountry) return "tos_country";

      if (!stripeComplete) return "stripe";

      return "done";
    }

    // --- NEW COLLECTION FLOW (creator onboarding) ---
    if (enrollmentFlow === FLOW.NEW_COLLECTION) {
      if (!isSignedIn) return "auth";

      if (!emailVerified) return "verify_email";

      // add creator role + collect details before payouts
      if (!isCreator || !hasCreatorDetails) return "creator_details";

      if (!effectiveCountry) return "tos_country";

      if (!stripeComplete) return "stripe";

      return "done";
    }

    // --- EXISTING COLLECTION FLOW ---
    if (enrollmentFlow === FLOW.EXISTING_COLLECTION) {
      if (!isSignedIn) return "auth";
      if (!emailVerified) return "verify_email";
      if (!isCreator) return "not_eligible";
      if (!hasCreatorDetails) return "creator_details";
      if (!effectiveCountry) return "tos_country";
      if (!stripeComplete) return "stripe";
      return "done";
    }

    return "auth";
  }, [
    authReady,
    enrollmentFlow,
    isSignedIn,
    emailVerified,
    isIP,
    isCreator,
    hasCreatorDetails,
    effectiveCountry,
    stripeComplete,
  ]);

  // ---------------------------
  // Actions
  // ---------------------------
  const doSignIn = async () => {
    if (!form.email || !form.password) {
      setMessage("Enter email and password.");
      setError(true);
      return;
    }
    setLoading(true);
    try {
      await login(form.email, form.password);
      setSuccess(true);
      setMessage("Signed in.");
    } catch (e) {
      setError(true);
      setMessage(e?.message || "Sign in failed.");
    } finally {
      setLoading(false);
    }
  };

  const doSignUp = async () => {
    const validFields = validateAuthInputs(form);
    if (!validFields?.status) {
      setMessage(validFields?.message || "Invalid inputs.");
      setError(true);
      return;
    }

    if (form.password !== form.confirmPassword) {
      setMessage("Passwords do not match.");
      setError(true);
      return;
    }

    // role depends on flow
    const role =
      enrollmentFlow === FLOW.NEW_COLLECTION
        ? "collection_creator"
        : enrollmentFlow === FLOW.IP_PROVIDER
        ? "ip_provider"
        : "";

    if (!role) {
      setMessage("Invalid flow selection.");
      setError(true);
      return;
    }

    // creator requires more details on account creation
    if (role === "collection_creator") {
      if (
        !form.fullName ||
        !form.username ||
        !form.email ||
        !form.collectionName ||
        !form.xHandle
      ) {
        setMessage("Please complete all required creator fields.");
        setError(true);
        return;
      }
      if (!form.logoFile) {
        setMessage("Logo is required for collection creators.");
        setError(true);
        return;
      }
    } else {
      if (!form.fullName || !form.username || !form.email) {
        setMessage("Please complete all required fields.");
        setError(true);
        return;
      }
    }

    setLoading(true);
    try {
      if (role === "collection_creator") {
        await signUp(form.username, form.email, form.password, {
          name: form.fullName,
          role,
          xHandle: form.xHandle,
          collectionName: form.collectionName,
          logoFile: form.logoFile,
          bannerFile: form.bannerFile || null,
          discord: sanitizeUrl(form.discord) || null,
          mintPage: sanitizeUrl(form.mintPage) || null,
        });
      } else {
        await signUp(form.username, form.email, form.password, {
          name: form.fullName,
          role,
        });
      }

      setSuccess(true);
      setMessage("Account created! Please check your email verification.");
    } catch (e) {
      setError(true);
      setMessage(e?.message || "Sign up failed.");
    } finally {
      setLoading(false);
    }
  };

  const sendVerification = async () => {
    setVerificationLoading(true);
    try {
      if (!auth.currentUser) throw new Error("No authenticated user.");
      await sendEmailVerification(auth.currentUser);
      setSuccess(true);
      setMessage(
        "Verification email sent. After verifying, refresh this page."
      );
    } catch (e) {
      setError(true);
      setMessage(e?.message || "Failed to send verification email.");
    } finally {
      setVerificationLoading(false);
    }
  };

  const addRoleToAccount = async (roleToAdd, options = {}) => {
    const { manageLoading = true, silent = false } = options;
    if (!userData?.id) return false;
    if (manageLoading) setLoading(true);
    try {
      const userRef = doc(db, "users", userData.id);
      const snap = await getDoc(userRef);
      const current = snap.data() || {};
      const currentRoles = Array.isArray(current.roles) ? current.roles : [];

      const nextRoles = currentRoles.includes(roleToAdd)
        ? currentRoles
        : [...currentRoles, roleToAdd];

      await updateDoc(userRef, {
        roles: nextRoles,
        updatedAt: new Date(),
      });

      if (!silent) {
        setSuccess(true);
        setMessage("Role updated.");
      }
      return true;
    } catch (e) {
      if (!silent) {
        setError(true);
        setMessage(e?.message || "Failed to update roles.");
      }
      return false;
    } finally {
      if (manageLoading) setLoading(false);
    }
  };

  const startStripeOnboarding = async () => {
    if (!userData?.id) return;
    if (!effectiveCountry) {
      setError(true);
      setMessage("Please select a country.");
      return;
    }

    setLoading(true);
    try {
      const functions = getFunctions();
      const createLink = httpsCallable(functions, "createStripeOnboardingLink");

      const userRef = doc(db, "users", userData.id);
      await updateDoc(userRef, { country: effectiveCountry });

      const { data } = await createLink({
        country: effectiveCountry,
        origin: window.location.origin,
        test: window.location.origin.includes("localhost"),
      });

      window.location.href = data.url;
    } catch (e) {
      setError(true);
      setMessage(e?.message || "Stripe onboarding failed.");
    } finally {
      setLoading(false);
    }
  };

  const confirmAndExit = async () => {
    confetti({
      particleCount: 150,
      angle: 90,
      spread: 180,
      startVelocity: 45,
      origin: { y: 0.5 },
      zIndex: 9999,
    });

    if (userData?.id && !userData.onboardingComplete) {
      const userRef = doc(db, "users", userData.id);
      await updateDoc(userRef, {
        onboardingComplete: true,
        updatedAt: new Date(),
      });
    }

    setTimeout(() => navigate("/"), 1200);
  };

  useEffect(() => {
    if (
      step !== "done" ||
      !userData?.id ||
      userData?.onboardingComplete ||
      completionWritePending
    ) {
      return;
    }

    let cancelled = false;
    (async () => {
      setCompletionWritePending(true);
      try {
        await updateDoc(doc(db, "users", userData.id), {
          onboardingComplete: true,
          updatedAt: new Date(),
        });
      } catch (e) {
        if (!cancelled) {
          console.error("Failed to auto-mark onboardingComplete:", e);
        }
      } finally {
        if (!cancelled) setCompletionWritePending(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    step,
    userData?.id,
    userData?.onboardingComplete,
    completionWritePending,
  ]);

  // ---------------------------
  // UI Blocks (per step)
  // ---------------------------
  const FlowTabs = () => (
    <Box sx={{ mb: 2.5, display: "flex", justifyContent: "center", overflowX: "auto" }}>
      <SegmentedControl
        size="sm"
        value={enrollmentFlow}
        onChange={setEnrollmentFlow}
        options={[
          { value: FLOW.EXISTING_COLLECTION, label: "Already In-Game" },
          { value: FLOW.NEW_COLLECTION, label: "New Collection" },
          { value: FLOW.IP_PROVIDER, label: "IP Provider" },
        ]}
      />
    </Box>
  );

  const VerifyEmailStep = () => (
    <Box sx={{ ...sectionCardSx }}>
      <Typography sx={{ fontWeight: 800, mb: 1 }}>Verify your email</Typography>
      <Typography sx={{ opacity: 0.9, mb: 2 }}>
        You must verify your email before continuing onboarding.
      </Typography>

      <Button
        fullWidth
        variant="contained"
        sx={{
          bgcolor: "#6f52ed",
          "&:hover": { bgcolor: "#5a3ed3" },
          fontWeight: 800,
        }}
        onClick={sendVerification}
        disabled={verificationLoading}
      >
        {verificationLoading ? "Sending..." : "Send Verification Email"}
      </Button>

      <Typography sx={{ mt: 2, opacity: 0.8, fontSize: 13 }}>
        After verifying, refresh this page.
      </Typography>
    </Box>
  );

  const AddRoleStep = () => {
    const roleLabel =
      enrollmentFlow === FLOW.IP_PROVIDER
        ? "IP Provider"
        : "Collection Creator";
    const roleToAdd =
      enrollmentFlow === FLOW.IP_PROVIDER
        ? "ip_provider"
        : "collection_creator";

    return (
      <Box sx={{ ...sectionCardSx }}>
        <Typography sx={{ fontWeight: 800, mb: 1 }}>
          Enable {roleLabel} on your account
        </Typography>
        <Typography sx={{ opacity: 0.9, mb: 2 }}>
          You’re signed in already — just add the role to your existing account
          to continue.
        </Typography>

        <Button
          fullWidth
          variant="contained"
          sx={{
            bgcolor: "#6f52ed",
            "&:hover": { bgcolor: "#5a3ed3" },
            fontWeight: 800,
          }}
          onClick={() => addRoleToAccount(roleToAdd)}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={22} sx={{ color: "#fff" }} />
          ) : (
            `Add ${roleLabel} Role`
          )}
        </Button>
      </Box>
    );
  };

  const CreatorDetailsStep = () => (
    <Box sx={{ ...sectionCardSx }}>
      <Typography sx={{ fontWeight: 800, mb: 1 }}>Creator details</Typography>
      <Typography sx={{ opacity: 0.9, mb: 2 }}>
        Add your collection details to continue.
      </Typography>

      <TextField
        fullWidth
        label="Name of Collection"
        variant="filled"
        sx={whiteFieldStyle}
        value={form.collectionName}
        onChange={handleChange("collectionName")}
      />
      <TextField
        fullWidth
        label="X User Handle"
        variant="filled"
        sx={whiteFieldStyle}
        value={form.xHandle}
        onChange={handleChange("xHandle")}
      />
      <TextField
        fullWidth
        label="Discord (optional)"
        placeholder="discord.gg/your-server"
        variant="filled"
        sx={whiteFieldStyle}
        value={form.discord}
        onChange={handleChange("discord")}
      />
      <TextField
        fullWidth
        label="Mint Page / NFT Link (optional)"
        placeholder="opensea.io/collection/your-collection"
        variant="filled"
        sx={whiteFieldStyle}
        value={form.mintPage}
        onChange={handleChange("mintPage")}
      />

      <Box sx={{ mt: 1 }}>
        <Typography sx={{ mb: 1, fontWeight: 700 }}>
          Collection Logo (required)
        </Typography>
        <Button component="label" variant="contained">
          Upload Logo
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={handleFile("logoFile")}
          />
        </Button>

        {(form.logoFile || creatorLogoValue) && (
          <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              src={
                form.logoFile
                  ? URL.createObjectURL(form.logoFile)
                  : creatorLogoValue
              }
              alt="logo preview"
              sx={{ width: 56, height: 56, border: "2px solid #6f52ed" }}
            />
            <Typography sx={{ color: "#ddd" }}>
              {form.logoFile ? form.logoFile.name : "Current logo"}
            </Typography>
          </Box>
        )}
      </Box>

      <Button
        fullWidth
        variant="contained"
        sx={{
          mt: 2,
          bgcolor: "#6f52ed",
          "&:hover": { bgcolor: "#5a3ed3" },
          fontWeight: 800,
        }}
        disabled={loading}
        onClick={async () => {
          // add creator role + save details
          if (
            !form.collectionName?.trim() ||
            !form.xHandle?.trim() ||
            (!form.logoFile && !creatorLogoValue)
          ) {
            setError(true);
            setMessage(
              "Collection name, X handle, and logo are required."
            );
            return;
          }
          setLoading(true);
          try {
            if (!isCreator) {
              const roleAdded = await addRoleToAccount("collection_creator", {
                manageLoading: false,
                silent: true,
              });
              if (!roleAdded) return;
            }

            const userRef = doc(db, "users", userData.id);
            const collectionRef = doc(db, "collections_onboarding", userData.id);

            const cleanName = form.collectionName.trim();
            const cleanHandle = form.xHandle.trim();
            const safeSlug =
              cleanName
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/(^-|-$)/g, "") || userData.id;

            let logoUrl = creatorLogoValue || "";
            if (form.logoFile) {
              logoUrl = await uploadImageAndGetUrl({
                file: form.logoFile,
                path: `collections_onboarding/${userData.id}/logo_${Date.now()}.png`,
              });
            }

            let bannerUrl = creatorCollectionMeta.bannerUrl || null;
            if (form.bannerFile) {
              bannerUrl = await uploadImageAndGetUrl({
                file: form.bannerFile,
                path: `collections_onboarding/${userData.id}/banner_${Date.now()}.png`,
              });
            }

            const nextDiscord = form.discord
              ? sanitizeUrl(form.discord)
              : null;
            const nextMintPage = form.mintPage
              ? sanitizeUrl(form.mintPage)
              : null;

            const collectionPayload = {
              name: cleanName,
              slug: safeSlug,
              creatorId: userData.id,
              xHandle: cleanHandle,
              logoUrl: logoUrl || "",
              bannerUrl: bannerUrl || null,
              discord: nextDiscord,
              mintPage: nextMintPage,
              updatedAt: serverTimestamp(),
            };

            if (!creatorCollectionMeta.exists) {
              collectionPayload.totalPoints = 0;
              collectionPayload.unlockedSlots = 0;
              collectionPayload.supporters = [];
              collectionPayload.approved = false;
              collectionPayload.createdAt = serverTimestamp();
            }

            await setDoc(collectionRef, collectionPayload, { merge: true });

            await updateDoc(userRef, {
              collectionName: cleanName,
              xHandle: cleanHandle,
              discord: nextDiscord,
              mintPage: nextMintPage,
              collection: {
                ...(userData?.collection || {}),
                id: userData.id,
                name: cleanName,
                slug: safeSlug,
                logoUrl: logoUrl || "",
                bannerUrl: bannerUrl || null,
              },
              updatedAt: new Date(),
            });

            setCreatorCollectionMeta((prev) => ({
              ...prev,
              exists: true,
              logoUrl: logoUrl || prev.logoUrl || "",
              bannerUrl: bannerUrl || prev.bannerUrl || "",
            }));

            setSuccess(true);
            setMessage("Creator details saved.");
          } catch (e) {
            setError(true);
            setMessage(e?.message || "Failed to save creator details.");
          } finally {
            setLoading(false);
          }
        }}
      >
        {loading ? (
          <CircularProgress size={22} sx={{ color: "#fff" }} />
        ) : (
          "Save & Continue"
        )}
      </Button>
    </Box>
  );

  const TosCountryStep = () => {
    const tosText = enrollmentFlow === FLOW.IP_PROVIDER ? IPTOS : CTOS;

    return (
      <Box sx={{ ...sectionCardSx }}>
        <Typography sx={{ fontWeight: 800, mb: 1 }}>
          Terms + Country (required for payouts)
        </Typography>

        <Box
          sx={{
            height: 200,
            overflowY: "auto",
            backgroundColor: "#ffffff10",
            borderRadius: 2,
            p: 2,
            mb: 2,
          }}
          onScroll={(e) => {
            const { scrollTop, scrollHeight, clientHeight } = e.target;
            if (scrollTop + clientHeight >= scrollHeight - 10)
              setTosScrolled(true);
          }}
        >
          <Typography
            sx={{
              color: "#fff",
              fontSize: 14,
              lineHeight: 1.5,
              whiteSpace: "pre-line",
            }}
          >
            {tosText}
          </Typography>
        </Box>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="country-label" sx={{ color: "#fff" }}>
            Select Country
          </InputLabel>
          <Select
            labelId="country-label"
            value={effectiveCountry}
            onChange={(e) => setCountry(e.target.value)}
            sx={{
              position: "relative",
              top: 6,
              color: "#fff",
              ".MuiOutlinedInput-notchedOutline": { borderColor: "#fff" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#6f52ed",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#6f52ed",
              },
            }}
          >
            {stripeConnectCountryCodes.map(({ country, code, flag }) => (
              <MenuItem key={code} value={code}>
                {flag} {country}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          fullWidth
          variant="contained"
          disabled={!tosScrolled || !effectiveCountry || loading}
          onClick={startStripeOnboarding}
          sx={{
            bgcolor: "#6f52ed",
            "&:hover": { bgcolor: "#5a3ed3" },
            fontWeight: 800,
          }}
        >
          {loading ? (
            <CircularProgress size={22} sx={{ color: "#fff" }} />
          ) : (
            "Continue to Stripe"
          )}
        </Button>
      </Box>
    );
  };

  const StripeStep = () => (
    <Box sx={{ ...sectionCardSx }}>
      <Typography sx={{ fontWeight: 800, mb: 1 }}>
        Complete Stripe onboarding
      </Typography>
      <Typography sx={{ opacity: 0.9, mb: 2 }}>
        Your Stripe setup isn’t complete yet. Continue onboarding to enable
        payouts.
      </Typography>

      <Button
        fullWidth
        variant="contained"
        disabled={loading || !effectiveCountry}
        onClick={startStripeOnboarding}
        sx={{
          bgcolor: "#6f52ed",
          "&:hover": { bgcolor: "#5a3ed3" },
          fontWeight: 800,
        }}
      >
        {loading ? (
          <CircularProgress size={22} sx={{ color: "#fff" }} />
        ) : (
          "Continue Stripe Onboarding"
        )}
      </Button>
    </Box>
  );

  const ExistingSubmitStep = () => (
    <Box sx={{ ...sectionCardSx }}>
      <Typography sx={{ fontWeight: 800, mb: 1 }}>
        Submit your collection details
      </Typography>
      <Typography sx={{ opacity: 0.9, mb: 2 }}>
        You’re signed in as a creator. Submit your latest details for review.
      </Typography>

      <TextField
        fullWidth
        label="Name of Collection"
        variant="filled"
        sx={whiteFieldStyle}
        value={form.collectionName}
        onChange={handleChange("collectionName")}
      />
      <TextField
        fullWidth
        label="X User Handle"
        variant="filled"
        sx={whiteFieldStyle}
        value={form.xHandle}
        onChange={handleChange("xHandle")}
      />

      <Button
        fullWidth
        variant="contained"
        disabled={loading}
        onClick={() => {
          setSuccess(true);
          setMessage("Details submitted! We’ll be in touch.");
        }}
        sx={{
          bgcolor: "#6f52ed",
          "&:hover": { bgcolor: "#5a3ed3" },
          fontWeight: 800,
        }}
      >
        Submit Details
      </Button>
    </Box>
  );

  const NotEligibleStep = () => (
    <Box sx={{ ...sectionCardSx, textAlign: "center" }}>
      <Typography sx={{ fontWeight: 800, mb: 1 }}>Not eligible</Typography>
      <Typography sx={{ opacity: 0.9 }}>
        “Already In-Game” is only for creators with collections already
        connected.
      </Typography>
    </Box>
  );

  const DoneStep = () => (
    <Box sx={{ ...sectionCardSx }}>
      <Typography variant="h5" sx={{ fontWeight: 900, mb: 1 }}>
        🎉 You’re onboarded!
      </Typography>

      {enrollmentFlow === FLOW.IP_PROVIDER && (
        <Typography sx={{ opacity: 0.9, mb: 2 }}>
          Your account is ready as an IP Provider. You can now participate in
          season enrollment and payouts.
        </Typography>
      )}

      {enrollmentFlow === FLOW.NEW_COLLECTION && (
        <Typography sx={{ opacity: 0.9, mb: 2 }}>
          Your creator onboarding is complete. Next step: share your enrollment
          page and rally your community.
        </Typography>
      )}

      <Button
        fullWidth
        variant="contained"
        sx={{
          bgcolor: "#6f52ed",
          "&:hover": { bgcolor: "#5a3ed3" },
          fontWeight: 900,
        }}
        onClick={confirmAndExit}
      >
        Confirm
      </Button>
    </Box>
  );

  const renderAuthStep = useCallback(() => {
    return (
      <Box sx={{ ...sectionCardSx }}>
        <Typography sx={{ fontWeight: 800, mb: 1 }}>
          {enrollmentFlow === FLOW.IP_PROVIDER
            ? "Become an IP Provider"
            : enrollmentFlow === FLOW.NEW_COLLECTION
            ? "Create your Creator Account"
            : "Sign in to manage your in-game collection"}
        </Typography>

        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <Button
            fullWidth
            variant={authMode === AUTH_MODE.SIGN_IN ? "contained" : "outlined"}
            onClick={() => setAuthMode(AUTH_MODE.SIGN_IN)}
            sx={{ textTransform: "none" }}
          >
            Sign In
          </Button>
          <Button
            fullWidth
            variant={authMode === AUTH_MODE.SIGN_UP ? "contained" : "outlined"}
            onClick={() => setAuthMode(AUTH_MODE.SIGN_UP)}
            sx={{ textTransform: "none" }}
          >
            Create Account
          </Button>
        </Box>

        {authMode === AUTH_MODE.SIGN_UP && (
          <>
            <TextField
              fullWidth
              label="Full Name"
              variant="filled"
              sx={whiteFieldStyle}
              value={form.fullName}
              onChange={handleChange("fullName")}
            />
            <TextField
              fullWidth
              label="Username (Gamertag)"
              variant="filled"
              sx={whiteFieldStyle}
              value={form.username}
              onChange={handleChange("username")}
            />
          </>
        )}

        <TextField
          fullWidth
          label="Email"
          type="email"
          variant="filled"
          sx={whiteFieldStyle}
          value={form.email}
          onChange={handleChange("email")}
        />

        <TextField
          fullWidth
          label="Password"
          type="password"
          variant="filled"
          sx={whiteFieldStyle}
          value={form.password}
          onChange={handleChange("password")}
        />

        {authMode === AUTH_MODE.SIGN_UP && (
          <>
            <TextField
              fullWidth
              label="Confirm Password"
              type="password"
              variant="filled"
              sx={whiteFieldStyle}
              value={form.confirmPassword}
              onChange={handleChange("confirmPassword")}
            />

            {enrollmentFlow === FLOW.NEW_COLLECTION && (
              <>
                <TextField
                  fullWidth
                  label="Collection Name"
                  variant="filled"
                  sx={whiteFieldStyle}
                  value={form.collectionName}
                  onChange={handleChange("collectionName")}
                />
                <TextField
                  fullWidth
                  label="X User Handle"
                  variant="filled"
                  sx={whiteFieldStyle}
                  value={form.xHandle}
                  onChange={handleChange("xHandle")}
                />
                <TextField
                  fullWidth
                  label="Discord (optional)"
                  placeholder="discord.gg/your-server"
                  variant="filled"
                  sx={whiteFieldStyle}
                  value={form.discord}
                  onChange={handleChange("discord")}
                />
                <TextField
                  fullWidth
                  label="Mint Page / NFT Link (optional)"
                  placeholder="opensea.io/collection/your-collection"
                  variant="filled"
                  sx={whiteFieldStyle}
                  value={form.mintPage}
                  onChange={handleChange("mintPage")}
                />
                <Box sx={{ mt: 1, mb: 2, textAlign: "left" }}>
                  <Typography sx={{ mb: 1, fontWeight: 700 }}>
                    Collection Logo (required)
                  </Typography>
                  <Button component="label" variant="contained">
                    Upload Logo
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={handleFile("logoFile")}
                    />
                  </Button>
                  {form.logoFile && (
                    <Typography sx={{ mt: 1, color: "#ddd" }}>
                      {form.logoFile.name}
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </>
        )}

        <Button
          fullWidth
          variant="contained"
          sx={{
            mt: 1,
            bgcolor: "#6f52ed",
            "&:hover": { bgcolor: "#5a3ed3" },
            fontWeight: 800,
          }}
          onClick={authMode === AUTH_MODE.SIGN_IN ? doSignIn : doSignUp}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={22} sx={{ color: "#fff" }} />
          ) : authMode === AUTH_MODE.SIGN_IN ? (
            "Sign In"
          ) : (
            "Create Account"
          )}
        </Button>
      </Box>
    );
  }, [
    sectionCardSx,
    enrollmentFlow,
    authMode,
    form,
    whiteFieldStyle,
    handleChange,
    handleFile,
    doSignIn,
    doSignUp,
    loading,
  ]);

  // ---------------------------
  // Render
  // ---------------------------
  return (
    <Grid
      container
      xs={12}
      sx={{
        height: "100vh",
        width: "100%",
        backgroundColor: "#07030f",
        backgroundImage: `linear-gradient(180deg, rgba(7,3,15,0.5) 0%, rgba(7,3,15,0.85) 100%), url(${bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        overflow: "hidden",
        position: "relative",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Toast onFinish={() => setLoading(false)} />
      <GlassIconButton
        label="Back"
        icon={<ArrowBackIosNewRounded />}
        size={40}
        onClick={() => navigate(-1)}
        sx={{ position: "absolute", top: 12, left: 12, zIndex: 2 }}
      />
      <DarkSurface>

      <Box
        sx={{
          ...glassPanelSx,
          borderRadius: "22px",
          p: { xs: 2.5, sm: 4, md: 5 },
          width: { xs: "90%", sm: "85%", md: "75%", lg: "65%" },
          maxWidth: { xs: 480, sm: 640, md: 960, lg: 1100 },
          maxHeight: { xs: "calc(100vh - 32px)", md: "calc(100vh - 140px)" },
          display: "flex",
          flexDirection: "column",
          color: "#fff",
          textAlign: "center",
          mx: "auto",
        }}
      >
        <FlowTabs />

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            pr: 1,
            "&::-webkit-scrollbar": { width: 8 },
            "&::-webkit-scrollbar-thumb": {
              background: "rgba(255,255,255,0.25)",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-track": { background: "transparent" },
          }}
        >
          {/* Header */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ ...eyebrowTextSx, color: colors.gold }}>
              Creators & IP partners
            </Typography>
            <Typography sx={{ ...displayTextSx, fontSize: { xs: "1.8rem", md: "2.3rem" }, lineHeight: 1.1, mb: 0.75 }}>
              Season 2 Enrollment
            </Typography>
            <Typography variant="body2" sx={{ color: colors.textSoft, maxWidth: 560, mx: "auto" }}>
              {enrollmentFlow === FLOW.IP_PROVIDER
                ? "Onboard as an IP Provider using your existing account or by creating a new one."
                : enrollmentFlow === FLOW.NEW_COLLECTION
                ? "Create a collection creator account and onboard to payouts."
                : "Sign in with your creator account tied to an existing in-game collection."}
            </Typography>
          </Box>

          {/* Step body */}
          <Box sx={{ display: "grid", gap: 2 }}>
            {step === "loading" && (
              <Box sx={{ ...sectionCardSx, textAlign: "center" }}>
                <CircularProgress sx={{ color: "#fff" }} />
                <Typography sx={{ mt: 2, opacity: 0.9 }}>Loading…</Typography>
              </Box>
            )}

            {step === "auth" && renderAuthStep()}

            {step === "verify_email" && <VerifyEmailStep />}

            {/* For IP_PROVIDER this is "add_role". For NEW_COLLECTION step is "creator_details" then role + details saved */}
            {step === "add_role" && <AddRoleStep />}
            {step === "creator_details" && <CreatorDetailsStep />}

            {step === "tos_country" && <TosCountryStep />}
            {step === "stripe" && <StripeStep />}

            {step === "existing_submit" && <ExistingSubmitStep />}
            {step === "not_eligible" && <NotEligibleStep />}

            {step === "done" && <DoneStep />}

            {/* Debug (optional) */}
            {/* <pre style={{ textAlign: "left", opacity: 0.7 }}>
              {JSON.stringify({ step, enrollmentFlow, isSignedIn, roles, emailVerified, effectiveCountry, stripeComplete }, null, 2)}
            </pre> */}
          </Box>

          {/* Signed-in hint / switch account */}
          {isSignedIn && (
            <Typography sx={{ mt: 2, opacity: 0.75, fontSize: 13 }}>
              Signed in as{" "}
              <span style={{ color: colors.gold }}>
                {userData?.email}
              </span>
            </Typography>
          )}
        </Box>
      </Box>
      </DarkSurface>
    </Grid>
  );
};

export default OnboardingScreen;